import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { TextField, Theme } from '@mui/material';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import { selectIsEditMod, selectStandard } from 'store/redux-store/placement-mods/selectors';
import { makeStyles } from 'tss-react/mui';

interface NotesProps {
  readOnly?: boolean;
}

export const Notes = ({ readOnly }: NotesProps) => {
  const { control, getValues, formState: { errors }, setValue } = useFormContext();
  const { t } = useTranslation();
  const notes = 'notes';

  const isModEdit = useSelector(selectIsEditMod);
  const standard = useSelector(selectStandard);
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const notesValue: string | null | undefined = useWatch({ name: 'notes' });
  const useStyles = makeStyles()((theme: Theme) => ({
    textField: {
      '& .MuiFilledInput-root': {
        backgroundColor: 'rgba(0,0,0,0.04)',
      },
      '& .MuiFilledInput-root:hover': {
        backgroundColor: 'rgba(0,0,0,0.04)',
        '@media (hover: none)': {
          backgroundColor: 'rgba(0,0,0,0.04)',
        },
      },
      '& .MuiFilledInput-root.Mui-disabled': {
        backgroundColor: 'rgba(0,0,0,0.04)',
      },
      '& .MuiFilledInput-root.Mui-disabled:before': {
        borderBottomStyle: 'solid',
      },
      '& .MuiFilledInput-root:hover.Mui-disabled:before': {
        borderBottom: '1px solid gray !important',
      },
      '& .MuiFilledInput-input': {
        cursor: 'default',
      },
      '& .MuiFilledInput-root:hover:before': {
        borderBottom: '1px solid gray !important',
      },
    },
    characterIndication: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
  }));
  const { classes } = useStyles();

  return (
    <>
      <Controller
        control={control}
        name={notes}
        rules={{
          maxLength: { value: 6000, message: 'notification.createTask.maxLimitLabel' },
        }}
        render={({ field: { onChange, value, ...rest } }) => (
          <TextField
            multiline
            className={classes.textField}
            fullWidth
            maxRows={5}
            id="bonusNotes"
            variant="filled"
            color="primary"
            autoComplete="off"
            label={t('placement.placementMods.bonusNotes')}
            inputProps={{
              background: 'rgba(0,0,0, 0.04)',
            }}
            InputProps={{
              readOnly: readOnly !== undefined ? readOnly : (standard ? true : !isModEdit),
              style: {
                pointerEvents: (readOnly ?? !(isModEdit || isSaveModApiCalling)) ? 'none' : 'auto',
              },
            }}
            defaultValue={getValues(notes)}
            onChange={e => {
              const isReadOnlyField = readOnly !== undefined ? readOnly : (standard ? true : !isModEdit);
              if (!isReadOnlyField && e.target.value?.length <= 6000) {
                setValue(notes, e.target.value);
                onChange(e.target.value);
              }
            }}
            error={!!errors?.notes}
            helperText={!isNullOrUndefined(errors?.notes) ? errors?.notes?.message : undefined}
            disabled={isSaveModApiCalling}
            value={getValues(notes)}
            required={false}
            {...rest}
          />
        )}
      />
      <div className={classes.characterIndication}>
        <span style={{ color: '#707070' }}>{notesValue ? notesValue?.length.toLocaleString() : 0}/6,000</span>
      </div>
    </>
  );
};
